import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Smartphone, ArrowLeft, KeyRound } from 'lucide-react';
import PublicPageFooter from '../components/PublicPageFooter';

const LoginPage: React.FC = () => {
  const location = useLocation();
  const redirectTo = (location.state as any)?.from || '/dashboard';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { login, verifyTwoFactor } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 2FA state
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [twoFactorMethod, setTwoFactorMethod] = useState<'totp' | 'sms'>('totp');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [smsSending, setSmsSending] = useState(false);

  useEffect(() => {
    const verification = searchParams.get('verification');
    const reset = searchParams.get('reset');

    const kicked = searchParams.get('kicked');
    const kickMessage = searchParams.get('message');

    if (kicked === 'true' && kickMessage) {
      setError(decodeURIComponent(kickMessage));
    } else if (verification === 'success') {
      setSuccess('Email verified successfully! You can now log in.');
    } else if (verification === 'invalid') {
      setError('Invalid verification link. Please try again or request a new verification email.');
    } else if (verification === 'expired') {
      setError('Verification link has expired. Please request a new verification email.');
    } else if (verification === 'error') {
      setError('Verification failed. Please try again or contact support.');
    } else if (reset === 'success') {
      setSuccess('Password reset successfully! You can now log in with your new password.');
    }
  }, [searchParams]);

  const sendSMSCode = async (token: string) => {
    setSmsSending(true);
    try {
      const response = await fetch('/api/2fa/sms/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ twoFactorToken: token })
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess('Verification code sent to ' + (data.maskedPhone || maskedPhone));
      } else {
        setError(data.error || 'Failed to send SMS code');
      }
    } catch (err) {
      setError('Failed to send SMS code');
    } finally {
      setSmsSending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await login(email, password);
      if (result.success) {
        navigate(redirectTo);
      } else if (result.requiresTwoFactor) {
        setTwoFactorToken(result.twoFactorToken || '');
        setTwoFactorMethod(result.twoFactorMethod || 'totp');
        setMaskedPhone(result.maskedPhone || '');
        setShowTwoFactor(true);
        setError('');

        // Auto-send SMS code if method is SMS
        if (result.twoFactorMethod === 'sms' && result.twoFactorToken) {
          await sendSMSCode(result.twoFactorToken);
        }
      } else if (result.needsVerification) {
        localStorage.setItem('verification_email', result.email || email);
        navigate('/verify-email-required');
      } else {
        setError('Invalid email or password');
      }
    } catch (err) {
      setError('An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwoFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await verifyTwoFactor(twoFactorToken, twoFactorCode, useBackupCode);
      if (result.success) {
        navigate(redirectTo);
      } else {
        setError(result.error || 'Invalid verification code');
      }
    } catch (err) {
      setError('Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowTwoFactor(false);
    setTwoFactorCode('');
    setTwoFactorToken('');
    setUseBackupCode(false);
    setError('');
    setSuccess('');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding (large area → use full logo) */}
      <div className="hidden lg:flex lg:w-1/2 header-enterprise flex-col justify-center items-center p-12">
        <div className="max-w-md text-center">
          <img
            src="/logo-full.png"
            alt="ProBizPay"
            className="mx-auto mb-6"
            style={{ maxWidth: '320px', height: 'auto' }}
          />
          <p className="text-xl text-steel-300 mb-8">Enterprise Platform</p>
          <div className="space-y-4 text-left">
            <div className="flex items-center gap-3 text-steel-300">
              <div className="h-2 w-2 rounded-full bg-construction-500"></div>
              <span>Streamlined project management</span>
            </div>
            <div className="flex items-center gap-3 text-steel-300">
              <div className="h-2 w-2 rounded-full bg-construction-500"></div>
              <span>AI-powered blueprint analysis</span>
            </div>
            <div className="flex items-center gap-3 text-steel-300">
              <div className="h-2 w-2 rounded-full bg-construction-500"></div>
              <span>Integrated payment processing</span>
            </div>
            <div className="flex items-center gap-3 text-steel-300">
              <div className="h-2 w-2 rounded-full bg-construction-500"></div>
              <span>Enterprise-grade security</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form or 2FA Verification */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-steel-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          {/* Mobile Logo — use full logo since this is centered with room */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="/logo-full.png"
              alt="ProBizPay"
              className="mx-auto mb-4"
              style={{ maxWidth: '240px', height: 'auto' }}
            />
            <p className="text-steel-500">Enterprise Platform</p>
          </div>

          {showTwoFactor ? (
            /* ==================== 2FA VERIFICATION ==================== */
            <div className="card-steel">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Shield className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-steel-900">Two-Factor Authentication</h2>
                  </div>
                </div>
                {twoFactorMethod === 'sms' ? (
                  <p className="text-steel-500 mt-2 flex items-center gap-2">
                    <Smartphone className="h-4 w-4" />
                    A verification code was sent to {maskedPhone}
                  </p>
                ) : (
                  <p className="text-steel-500 mt-2">
                    {useBackupCode
                      ? 'Enter one of your backup codes'
                      : 'Enter the 6-digit code from your authenticator app'}
                  </p>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-enterprise flex items-center gap-2 mb-4">
                  <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {error}
                </div>
              )}
              {success && (
                <div className="bg-success-50 border border-success-200 text-success-700 px-4 py-3 rounded-enterprise flex items-center gap-2 mb-4">
                  <svg className="h-5 w-5 text-success-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {success}
                </div>
              )}

              <form onSubmit={handleTwoFactorSubmit} className="space-y-5">
                <div>
                  <label htmlFor="twoFactorCode" className="block text-sm font-medium text-steel-700 mb-1">
                    {useBackupCode ? 'Backup Code' : 'Verification Code'}
                  </label>
                  <input
                    id="twoFactorCode"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={useBackupCode ? 8 : 6}
                    className="input-field text-center text-2xl tracking-widest"
                    placeholder={useBackupCode ? 'XXXXXXXX' : '000000'}
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\s/g, ''))}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading || twoFactorCode.length < (useBackupCode ? 8 : 6)}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </>
                  ) : (
                    'Verify & Sign In'
                  )}
                </button>

                <div className="flex flex-col gap-3">
                  {twoFactorMethod === 'sms' && (
                    <button
                      type="button"
                      onClick={() => sendSMSCode(twoFactorToken)}
                      disabled={smsSending}
                      className="text-sm font-medium text-primary-600 hover:text-primary-500 transition-colors flex items-center justify-center gap-1"
                    >
                      <Smartphone className="h-4 w-4" />
                      {smsSending ? 'Sending...' : 'Resend Code'}
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setUseBackupCode(!useBackupCode);
                      setTwoFactorCode('');
                      setError('');
                    }}
                    className="text-sm font-medium text-steel-600 hover:text-steel-800 transition-colors flex items-center justify-center gap-1"
                  >
                    <KeyRound className="h-4 w-4" />
                    {useBackupCode ? 'Use verification code instead' : 'Use a backup code'}
                  </button>

                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="text-sm font-medium text-steel-500 hover:text-steel-700 transition-colors flex items-center justify-center gap-1"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to login
                  </button>
                </div>
              </form>
            </div>
          ) : (
            /* ==================== LOGIN FORM ==================== */
            <>
              <div className="card-steel">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-steel-900">Welcome back</h2>
                  <p className="text-steel-500 mt-1">Sign in to your account to continue</p>
                </div>

                <form className="space-y-5" onSubmit={handleSubmit}>
                  {success && (
                    <div className="bg-success-50 border border-success-200 text-success-700 px-4 py-3 rounded-enterprise flex items-center gap-2">
                      <svg className="h-5 w-5 text-success-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {success}
                    </div>
                  )}
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-enterprise flex items-center gap-2">
                      <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {error}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-steel-700 mb-1">
                        Email address
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        className="input-field"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-steel-700 mb-1">
                        Password
                      </label>
                      <input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        required
                        className="input-field"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-end">
                    <Link to="/forgot-password" className="text-sm font-medium text-primary-600 hover:text-primary-500 transition-colors">
                      Forgot your password?
                    </Link>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Signing in...
                      </>
                    ) : (
                      'Sign in'
                    )}
                  </button>

                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-steel-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-steel-500">New to ProBizPay?</span>
                    </div>
                  </div>

                  <Link
                    to="/signup"
                    className="btn-secondary w-full text-center block"
                  >
                    Create an account
                  </Link>
                </form>
              </div>

              <p className="mt-6 text-center text-sm text-steel-500">
                By signing in, you agree to our{' '}
                <Link to="/terms" className="text-primary-600 hover:text-primary-500 underline underline-offset-2">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-primary-600 hover:text-primary-500 underline underline-offset-2">Privacy Policy</Link>
              </p>
            </>
          )}
          <PublicPageFooter />
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
